import React, { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/Button';
import SafeIcon from '../../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import supabase from '../../../supabase/supabase';
import { emailService } from '../../../services/emailService';
import { templates as staticTemplates } from '../../../services/emailTemplates';

const { FiMail, FiSend, FiCheckCircle, FiAlertCircle, FiCopy, FiEye, FiCode } = FiIcons;

export default function AdminEmailTemplates() {
  const [activeTab, setActiveTab] = useState('templates');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [viewMode, setViewMode] = useState('preview');

  // Broadcast states
  const [broadcastAudience, setBroadcastAudience] = useState('test');
  const [testEmail, setTestEmail] = useState('');
  const [broadcastSubject, setBroadcastSubject] = useState('');
  const [broadcastBody, setBroadcastBody] = useState('<p>Write your message here...</p>');
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  const [broadcastStatus, setBroadcastStatus] = useState(null);

  useEffect(() => {
    try {
      const mockDetails = {
        customerName: 'John Doe',
        customerEmail: 'john@example.com',
        customerPhone: '+94 77 123 4567',
        barberName: 'Elite Barber',
        barberEmail: 'barber@example.com',
        serviceName: 'Premium Haircut',
        date: new Date().toLocaleDateString(),
        time: '10:00 AM',
        price: '1500',
        address: '123 Main St, Colombo',
        manageLink: 'https://bookotg.com/#/login',
        reason: 'Schedule conflict'
      };

      const enriched = [
        { id: 1, slug: 'supabase_confirm', name: 'Account Verification', subject: 'Verify your BookOTG account', body: staticTemplates.supabaseConfirm(), category: 'Auth' },
        { id: 2, slug: 'password_reset', name: 'Password Reset', subject: 'Reset your BookOTG password', body: staticTemplates.passwordReset(), category: 'Auth' },
        { id: 3, slug: 'welcome_customer', name: 'Welcome Email', subject: 'Welcome to the Elite!', body: staticTemplates.welcomeEmail('John Doe', 'customer'), category: 'Auth' },
        { id: 4, slug: 'booking_received', name: 'New Request (To Barber)', subject: 'Action Required: New Request', body: staticTemplates.bookingReceived(mockDetails), category: 'Booking' },
        { id: 5, slug: 'booking_confirmed', name: 'Booking Confirmed (To Customer)', subject: 'Your Booking is Confirmed!', body: staticTemplates.bookingConfirmed(mockDetails), category: 'Booking' },
        { id: 6, slug: 'booking_cancelled', name: 'Booking Cancelled', subject: 'Urgent: Appointment Cancelled', body: staticTemplates.bookingCancelled(mockDetails), category: 'Booking' },
        { id: 7, slug: 'booking_completed', name: 'Booking Completed', subject: 'Thank You!', body: staticTemplates.bookingCompleted(mockDetails), category: 'Booking' },
      ];

      setTemplates(enriched);
      if (enriched.length > 0) setSelectedTemplate(enriched[0]);
    } catch (err) {
      console.error("Error building templates:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(selectedTemplate.body);
    setStatus({ type: 'success', message: 'HTML copied to clipboard!' });
    setTimeout(() => setStatus(null), 3000);
  };

  const handleSendBroadcast = async () => {
    if (!broadcastSubject.trim() || !broadcastBody.trim()) {
      setBroadcastStatus({ type: 'error', message: 'Subject and Body are required.' });
      return;
    }

    setSendingBroadcast(true);
    setBroadcastStatus(null);

    try {
      let targetEmails = [];

      if (broadcastAudience === 'test') {
        if (!testEmail || !testEmail.includes('@')) throw new Error("Enter a valid test email address.");
        targetEmails = [testEmail];
      } else {
        let query = supabase.from('profiles_1740423033529').select('email').not('email', 'is', null);
        if (broadcastAudience === 'customers') query = query.eq('role', 'customer');
        else if (broadcastAudience === 'barbers') query = query.eq('role', 'barber');

        const { data, error } = await query;
        if (error) throw error;
        targetEmails = data.map(u => u.email).filter(e => e);
      }

      if (targetEmails.length === 0) throw new Error("No users found for the selected audience.");

      let successCount = 0;
      let lastError = null;

      for (const email of targetEmails) {
        const res = await emailService.sendPromotionalEmail(email, broadcastSubject, broadcastBody);
        if (res.success) {
          successCount++;
        } else {
          lastError = res.error;
        }
      }

      if (successCount > 0) {
        setBroadcastStatus({ type: 'success', message: `Successfully sent to ${successCount} recipient(s).` });
        if (broadcastAudience !== 'test') {
          setBroadcastSubject('');
          setBroadcastBody('<p>Write your message here...</p>');
        }
      } else {
        setBroadcastStatus({ type: 'error', message: lastError || "Failed to send emails." });
      }

    } catch (err) {
      setBroadcastStatus({ type: 'error', message: err.message });
    } finally {
      setSendingBroadcast(false);
    }
  };

  if (loading) return (
    <div className="p-20 text-center animate-pulse">
      <div className="w-12 h-12 border-4 border-brand-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
      <p className="text-gray-400 font-black uppercase tracking-widest text-[10px]">Syncing Communications...</p>
    </div>
  );

  return (
    <div className="animate-in fade-in duration-500">
      <div className="mb-8 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-black text-brand-dark tracking-tight">Communications</h1>
          <p className="text-gray-500 font-medium italic mt-1">Manage system templates and send mass broadcasts.</p>
        </div>
        <div className="flex bg-white p-1 rounded-2xl border border-gray-200 shadow-sm">
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'templates' ? 'bg-brand-dark text-white' : 'text-gray-500 hover:text-brand-dark'}`}
          >
            Templates
          </button>
          <button
            onClick={() => setActiveTab('broadcast')}
            className={`px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all ${activeTab === 'broadcast' ? 'bg-brand-primary text-white' : 'text-gray-500 hover:text-brand-primary'}`}
          >
            Broadcast
          </button>
        </div>
      </div>

      {activeTab === 'templates' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-3 space-y-3">
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="p-4 bg-gray-50 border-b border-gray-100">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest text-center">System Emails</p>
              </div>
              <div className="divide-y divide-gray-50">
                {templates.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setSelectedTemplate(t)}
                    className={`w-full text-left p-4 hover:bg-gray-50 transition-colors flex flex-col ${selectedTemplate?.id === t.id ? 'bg-brand-primary/5 border-l-4 border-brand-primary' : 'border-l-4 border-transparent'}`}
                  >
                    <span className={`font-black text-xs truncate ${selectedTemplate?.id === t.id ? 'text-brand-primary' : 'text-brand-dark'}`}>{t.name}</span>
                    <span className="text-[9px] text-gray-400 uppercase font-black tracking-widest mt-1">{t.category}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="lg:col-span-9">
            {selectedTemplate && (
              <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm flex flex-col min-h-[600px]">
                <div className="p-4 border-b border-gray-100 flex flex-wrap gap-4 justify-between items-center bg-gray-50/50">
                  <div className="flex bg-gray-200 p-1 rounded-xl">
                    <button onClick={() => setViewMode('preview')} className={`px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2 ${viewMode === 'preview' ? 'bg-white text-brand-dark shadow-sm' : 'text-gray-500'}`}>
                      <SafeIcon icon={FiEye} /> Preview
                    </button>
                    <button onClick={() => setViewMode('edit')} className={`px-4 py-1.5 rounded-lg text-xs font-black uppercase tracking-widest transition-all flex items-center gap-2 ${viewMode === 'edit' ? 'bg-white text-brand-dark shadow-sm' : 'text-gray-500'}`}>
                      <SafeIcon icon={FiCode} /> HTML
                    </button>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handleCopy} className="h-9 px-4 font-black text-xs uppercase tracking-widest border-gray-200">
                      <SafeIcon icon={FiCopy} className="mr-2" /> Copy HTML
                    </Button>
                  </div>
                </div>

                <div className="p-8 space-y-6 flex-1 flex flex-col">
                  {status && (
                    <div className={`p-4 rounded-2xl flex items-center gap-3 animate-in slide-in-from-top-2 ${status.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                      <SafeIcon icon={status.type === 'success' ? FiCheckCircle : FiAlertCircle} />
                      <p className="text-xs font-black uppercase tracking-widest">{status.message}</p>
                    </div>
                  )}

                  <div className="flex-shrink-0">
                    <label className="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Email Subject</label>
                    <input type="text" readOnly value={selectedTemplate.subject} className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none font-bold text-brand-dark" />
                  </div>

                  <div className="flex-1 flex flex-col">
                    <label className="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Template Content</label>
                    {viewMode === 'edit' ? (
                      <textarea
                        readOnly
                        value={selectedTemplate.body}
                        className="w-full flex-1 p-6 bg-brand-dark text-blue-100 font-mono text-xs border border-gray-200 rounded-2xl outline-none resize-none"
                        spellCheck="false"
                      />
                    ) : (
                      <div className="flex-1 border border-gray-100 rounded-[2rem] overflow-hidden bg-gray-100 p-4 min-h-[400px]">
                        <iframe
                          srcDoc={selectedTemplate.body}
                          title="Email Preview"
                          className="w-full h-full bg-white rounded-2xl shadow-sm"
                          frameBorder="0"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'broadcast' && (
        <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-sm overflow-hidden animate-in fade-in max-w-4xl mx-auto">
          <div className="p-8 border-b border-gray-50 bg-gray-50/30 text-center">
            <h2 className="text-2xl font-black text-brand-dark">Send Mass Broadcast</h2>
            <p className="text-sm text-gray-500 font-medium mt-1">Direct communication with your platform users.</p>
          </div>

          <div className="p-8 space-y-8">
            {broadcastStatus && (
              <div className={`p-4 rounded-2xl flex items-center gap-3 animate-in slide-in-from-top-2 ${broadcastStatus.type === 'success' ? 'bg-green-50 text-green-700 border border-green-100' : 'bg-red-50 text-red-600 border border-red-100'}`}>
                <SafeIcon icon={broadcastStatus.type === 'success' ? FiCheckCircle : FiAlertCircle} />
                <p className="text-xs font-black uppercase tracking-widest">{broadcastStatus.message}</p>
              </div>
            )}

            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase mb-3 tracking-widest text-center">Target Audience</label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {['test', 'customers', 'barbers', 'all'].map((target) => (
                  <button
                    key={target}
                    onClick={() => setBroadcastAudience(target)}
                    className={`py-3 rounded-xl border-2 text-[10px] font-black uppercase tracking-widest transition-all ${broadcastAudience === target ? 'border-brand-primary bg-brand-primary/5 text-brand-primary' : 'border-gray-50 text-gray-400 hover:border-gray-200'}`}
                  >
                    {target === 'test' ? 'Test Email' : target}
                  </button>
                ))}
              </div>
            </div>

            {broadcastAudience === 'test' && (
              <div className="animate-in fade-in slide-in-from-top-2">
                <label className="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Test Email Address</label>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:border-brand-primary font-bold text-brand-dark"
                />
              </div>
            )}

            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Subject</label>
              <input
                type="text"
                value={broadcastSubject}
                onChange={(e) => setBroadcastSubject(e.target.value)}
                placeholder="Exclusive Offer for BookOTG Users! 🎉"
                className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:border-brand-primary font-bold text-brand-dark"
              />
            </div>

            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase mb-2 tracking-widest">Message Body (Rich Text / HTML)</label>
              <textarea
                value={broadcastBody}
                onChange={(e) => setBroadcastBody(e.target.value)}
                className="w-full p-6 bg-gray-50 border border-gray-100 rounded-2xl outline-none focus:border-brand-primary font-medium min-h-[200px]"
                placeholder="Hello BookOTG users..."
              />
            </div>

            <div className="pt-4 border-t border-gray-100 flex justify-end">
              <Button onClick={handleSendBroadcast} disabled={sendingBroadcast} className="px-10 py-5 font-black shadow-xl rounded-2xl text-lg">
                {sendingBroadcast ? 'Dispatching...' : 'Dispatch Broadcast'}
                <SafeIcon icon={FiSend} className="ml-2" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}